/* 4Warehouse product quantity estimate. Does not create discounts or orders. */
(() => {
  'use strict';
  const LIMIT = 999999;
  function rules(variant) {
    const step = Math.max(1, Math.trunc(Number(variant?.step) || 1));
    const min = Math.max(step, Math.ceil((Number(variant?.min) || 1) / step) * step);
    const configuredMax = Number(variant?.max);
    const max = Math.floor((configuredMax > 0 ? Math.min(LIMIT, configuredMax) : LIMIT) / step) * step;
    return { min, max, step };
  }
  function validQuantity(value, rule) {
    if (String(value).trim() === '') return false;
    const q = Number(value);
    return Number.isSafeInteger(q) && q >= rule.min && q <= rule.max && q % rule.step === 0;
  }
  function snap(value, rule) {
    const n = Number(value);
    const q = Number.isFinite(n) ? Math.round(n / rule.step) * rule.step : rule.min;
    return Math.max(rule.min, Math.min(rule.max, q));
  }
  function rangeMax(q, rule) {
    const target = Math.max(100, rule.min, q);
    return Math.min(rule.max, Math.ceil(target / rule.step) * rule.step);
  }
  function subtotal(price, q) {
    if (!Number.isSafeInteger(price) || price < 0 || !Number.isSafeInteger(q) || q < 1) return null;
    const amount = price * q;
    return Number.isSafeInteger(amount) ? amount : null;
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = {rules,validQuantity,snap,rangeMax,subtotal};
  if (typeof customElements === 'undefined' || customElements.get('wh-quantity-calculator')) return;
  class QuantityCalculator extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.form = document.getElementById(this.dataset.formId);
      this.quantity = this.form?.querySelector('[name="quantity"]');
      if (!this.form || !this.quantity) return;
      try { this.data = JSON.parse(this.querySelector('[data-calc-data]').textContent); } catch { return; }
      this.ready = true;
      this.range = this.querySelector('input[type="range"]');
      this.total = this.querySelector('[data-calc-total]');
      this.message = this.querySelector('[data-calc-message]');
      this.format = new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {style:'currency',currency:this.data.currency,currencyDisplay:'code'});
      this.variant = this.data.variants.find(v => String(v.id) === String(this.data.selected)) || null;
      this.listeners = [];
      const listen = (target, name, fn) => { if (target) { target.addEventListener(name,fn); this.listeners.push([target,name,fn]); } };
      listen(this.range, 'input', () => this.setQuantity(this.range.value));
      this.querySelectorAll('[data-calc-preset]').forEach(button => listen(button, 'click', () => this.setQuantity(button.dataset.calcPreset)));
      listen(this.quantity, 'input', () => this.update());
      listen(this.quantity, 'change', () => this.update());
      listen(this.form.querySelector('[name="id"]'), 'change', () => {
        this.variant = this.data.variants.find(v => String(v.id) === this.form.querySelector('[name="id"]').value) || null;
        this.applyVariant();
      });
      this.variantListener = event => {
        if (event.detail?.form !== this.form) return;
        this.variant = this.data.variants.find(v => String(v.id) === String(event.detail.variant?.id)) || null;
        this.applyVariant();
      };
      listen(document, 'on:variant:change', this.variantListener);
      this.applyVariant();
      this.hidden = false;
    }
    disconnectedCallback() {
      this.listeners?.forEach(([target,name,fn]) => target.removeEventListener(name,fn));
      this.ready = false;
    }
    applyVariant() {
      this.rule = rules(this.variant);
      this.quantity.min = this.rule.min;
      this.quantity.max = this.rule.max;
      this.quantity.step = this.rule.step;
      // A variant change may impose a new order increment or minimum.
      if (this.variant && !validQuantity(this.quantity.value,this.rule)) this.quantity.value = snap(this.quantity.value,this.rule);
      this.range.min = this.rule.min; this.range.step = this.rule.step;
      this.range.disabled = !this.variant || !this.variant.available;
      const option = this.querySelector('[data-calc-option]');
      option.textContent = this.variant && this.variant.title !== 'Default Title' ? this.variant.title : '';
      option.hidden = !option.textContent;
      this.querySelectorAll('[data-calc-preset]').forEach(b => { b.disabled = this.range.disabled || !validQuantity(b.dataset.calcPreset,this.rule); });
      this.update();
    }
    setQuantity(value) {
      if (this.range.disabled) return;
      this.quantity.value = snap(value,this.rule);
      this.quantity.dispatchEvent(new Event('input',{bubbles:true}));
      this.quantity.dispatchEvent(new Event('change',{bubbles:true}));
    }
    update() {
      const q = Number(this.quantity.value), valid = validQuantity(this.quantity.value,this.rule);
      this.querySelector('[data-calc-quantity]').textContent = valid ? String(q) : '—';
      this.range.max = rangeMax(valid ? q : this.rule.min,this.rule);
      this.range.value = valid ? q : this.rule.min;
      this.range.setAttribute('aria-valuetext',valid ? `${q} selected` : 'Enter a valid quantity');
      this.querySelector('[data-calc-min]').textContent = this.rule.min;
      this.querySelector('[data-calc-max]').textContent = this.range.max;
      const fill = (Number(this.range.value)-this.rule.min) / (Number(this.range.max)-this.rule.min || 1) * 100;
      this.range.style.setProperty('--wh-range-fill',fill+'%');
      this.querySelectorAll('[data-calc-preset]').forEach(b => b.setAttribute('aria-pressed',String(valid && Number(b.dataset.calcPreset)===q)));
      const amount = this.variant && valid ? subtotal(this.variant.price,q) : null;
      this.total.textContent = amount === null ? '—' : this.format.format(amount / 100);
      this.querySelector('[data-calc-equation]').textContent = amount === null ? '' : `${q} × ${this.format.format(this.variant.price / 100)}`;
      let message = '';
      if (!this.variant) message = 'Choose a product option to calculate a subtotal.';
      else if (!valid) message = `Enter a whole quantity from ${this.rule.min} to ${this.rule.max}${this.rule.step>1 ? ', in multiples of '+this.rule.step : ''}.`;
      else if (!this.variant.available) message = 'This option is unavailable to buy. Add it to your enquiry for a stock check.';
      else if (Number.isInteger(this.variant.stock) && q > this.variant.stock) message = `Only ${this.variant.stock} currently shown in stock. Enquire to confirm supply for larger quantities.`;
      this.message.textContent = message; this.message.hidden = !message;
      this.quantity.setAttribute('aria-invalid',String(!valid));
    }
  }
  customElements.define('wh-quantity-calculator',QuantityCalculator);
})();
